import { Datepicker } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useClearUserUrlLog } from '@/entities/user-url-log';
import { toastSuccess } from '@/shared/lib';
import { ConfirmationButton, Icon } from '@/shared/ui';

type Props = {
  /** Вызывается после успешной очистки — страница сбрасывает список к первой порции. */
  onCleared: () => void;
};

/**
 * Контрол очистки лога URL по периоду. Виден только пользователям с разрешением
 * ADMIN_USER_LOG. Пользователь выбирает момент (date-time) и подтверждает удаление —
 * все записи старше выбранного момента удаляются, после чего список перезапрашивается.
 */
export const UserUrlLogClearControl: FC<Props> = ({ onCleared }) => {
  const { t } = useTranslation('userUrlLog');
  const canClear = useHasPermission('ADMIN_USER_LOG');
  const [before, setBefore] = useState('');
  const { mutateAsync, isPending } = useClearUserUrlLog();

  if (!canClear) return null;

  const handleConfirm = async () => {
    if (!before) return;
    const { deleted } = await mutateAsync({ before });
    toastSuccess(t('clear.success', { count: deleted }));
    onCleared();
  };

  return (
    <div className="flex items-end gap-2">
      <Datepicker
        mode="single"
        showTimeInputs
        value={before}
        onChange={(value) => setBefore(value as string)}
        label={t('clear.before')}
        placeholder={t('clear.beforePlaceholder')}
      />
      <ConfirmationButton
        onConfirm={handleConfirm}
        isLoadingConfirm={isPending}
        title={t('clear.confirmTitle')}
        description={t('clear.confirmDescription')}
        trigger={{
          children: t('clear.action'),
          icon: <Icon type="trash" />,
          variant: 'outline',
          color: 'destructive',
          disabled: !before,
        }}
      />
    </div>
  );
};
